import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBrandEntryScreen } from './hooks/useBrandEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-base text-on-surface outline-none placeholder:text-on-surface-variant transition-all focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder, required = false }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={4}
        className="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-base text-on-surface outline-none placeholder:text-on-surface-variant transition-all focus:ring-2 focus:ring-primary/20 resize-y"
      />
    </label>
  );
}

export default function BrandEntryScreen() {
  const { state, actions } = useBrandEntryScreen();

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-8">
      {/* Hero Breadcrumb Header */}
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Masters</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Brand Master</span>
          </div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Brand' : 'Create Brand'}
          </h1>
          <p className="mt-2 max-w-xl text-base text-on-surface-variant">
            Define product brand labels and manufacturing details.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-3">
          <Link
            to="/masters/brand/list"
            className="inline-flex items-center justify-center rounded-xl bg-secondary-container px-7 py-3 text-sm font-bold text-secondary"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center rounded-xl bg-primary px-8 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {state.success ? 'Success! ✓' : state.isSaving ? 'Saving...' : 'Save Brand'}
          </button>
        </div>
      </section>

      {/* Loading State overlay */}
      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-14 text-center text-sm text-on-surface-variant">
          Loading brand details...
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-4">
                <div className="h-10 w-1.5 rounded-full bg-primary" />
                <h2 className="text-3xl font-bold text-on-surface">General Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-8">
                <TextField
                  label="Brand Name"
                  required
                  value={state.form.name}
                  onChange={(val) => actions.patchForm('name', val)}
                  placeholder="e.g. TATA"
                />

                <TextAreaField
                  label="Description"
                  value={state.form.description}
                  onChange={(val) => actions.patchForm('description', val)}
                  placeholder="e.g. Manufacturer specifications and brand label guidelines."
                />
              </div>
            </article>
          </div>

          {/* Right Column: Status & Metadata card */}
          <div className="col-span-12 space-y-6 lg:col-span-4">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8 flex flex-col gap-6">
              <div>
                <h3 className="text-lg font-bold text-on-surface mb-1">Operational Status</h3>
                <p className="text-xs text-on-surface-variant">Set visibility status for the brand.</p>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={state.operationalStatus === 'active'}
                    onChange={() => actions.setOperationalStatus('active')}
                    className="accent-primary"
                  />
                  <span className="text-sm font-semibold text-on-surface">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={state.operationalStatus === 'inactive'}
                    onChange={() => actions.setOperationalStatus('inactive')}
                    className="accent-primary"
                  />
                  <span className="text-sm font-semibold text-on-surface">Archived</span>
                </label>
              </div>

              <hr className="border-outline-variant/20" />

              <div>
                <h3 className="text-lg font-bold text-on-surface mb-1">Completeness</h3>
                <div className="mt-3 flex items-center gap-4">
                  <div className="h-2.5 w-full rounded-full bg-surface-container-high overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${state.completionPercent}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-on-surface-variant font-mono">{state.completionPercent}%</span>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  );
}
